import React, { FC, useCallback } from 'react';

import { Editor } from '@tiptap/react';
import { Box, Menu, MenuItem, Paper } from '@mui/material';

import { ReactComponent as ImageIcon } from '../../../../assets/svg/image-add-line.svg';
import useEditorToolbar from '../../../../hooks/Editor/useEditorToolbar';
import genericClasses from '../../../../theme/GenericClasses';
import strings from '../../../../localization';
import CustomButton from '../../CustomButton';
import styles from '../styles';

import EditorToolbarItems from './EditorToolbarItems/EditorToolbarItems';
import { Constants } from '../../../../helpers/Constants';
import {
  getConditionTags,
} from '../extensions/EmailCondition';

interface IEditorToolbar {
  conditionTag?: string;
  conditionTagOptions?: Array<{ key: string; value: string }>;
  editor: Editor | null;
  placeholderPluginOptions?: Array<Record<string, string>>;
}

const EditorToolbar: FC<IEditorToolbar> = ({
  conditionTag,
  conditionTagOptions,
  editor,
  placeholderPluginOptions,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<
    null | HTMLElement | undefined
  >(null);
  const [conditionAnchorEl, setConditionAnchorEl] = React.useState<
    null | HTMLElement | undefined
  >(null);
  const open = Boolean(anchorEl);
  const conditionOpen = Boolean(conditionAnchorEl);

  const inputFile = React.useRef<HTMLInputElement>(null);
  const { toolbarConfig, handleFileUpload } = useEditorToolbar({ editor });

  const handleOpen = (event?: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event?.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleConditionOpen = (event?: React.MouseEvent<HTMLButtonElement>) => {
    setConditionAnchorEl(event?.currentTarget);
  };

  const handleConditionClose = () => {
    setConditionAnchorEl(null);
  };

  const insertPlaceholder = (value: string) =>
    () => {
      if (!value) return;

      handleClose();

      const formattedPlaceholder = `{{${value}}}`;
      editor?.chain().focus().insertContent(formattedPlaceholder).run();
    };

  const insertCondition = (selectedConditionTag = conditionTag) =>
    () => {
      if (!editor) return;

      handleConditionClose();

      const { from, to, empty } = editor.state.selection;
      const transaction = editor.state.tr;
      const { closeTag, openTag } = getConditionTags(selectedConditionTag);

      if (empty) {
        transaction.insertText(
          `${openTag}Conditional content${closeTag}`,
          from,
        );
      } else {
        transaction
          .insertText(closeTag, to)
          .insertText(openTag, from);
      }

      editor.view.dispatch(transaction);
      editor.commands.focus();
    };

  const hasConditionOptions = !!conditionTagOptions?.length;

  const imageIconClick = () => {
    inputFile.current?.click();
  };

  const ImageUploadIcon = useCallback(
    () =>
      (
        <>
          <ImageIcon height={20} width={20} />
          <input
            ref={inputFile}
            style={genericClasses.hidden}
            type="file"
            onChange={handleFileUpload}
          />
        </>
      ),
    [handleFileUpload, inputFile],
  );

  return (
    <Paper sx={styles.toolbarContainer} elevation={1}>
      {toolbarConfig.map((item) => {
        const { key, icon, handleClick, isActive, title } = item;
        return (
          <EditorToolbarItems
            Icon={icon}
            handleClick={handleClick}
            key={key}
            isActive={!!isActive}
            title={title}
          />
        );
      })}
      <EditorToolbarItems
        Icon={ImageUploadIcon}
        handleClick={imageIconClick}
        isActive={false}
        title="Image"
      />

      {placeholderPluginOptions && (
        <Box sx={styles.placeholderContainer}>
          <CustomButton
            onClick={handleOpen}
            label={strings.addPlaceholder}
            variant={Constants.variant.text}
          />

          <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
            {placeholderPluginOptions?.map((opt) =>
              (
                <MenuItem key={opt.key} onClick={insertPlaceholder(opt.key)}>
                  {opt.label}
                </MenuItem>
              ))}
          </Menu>

          <CustomButton
            onClick={hasConditionOptions ? handleConditionOpen : insertCondition()}
            label="Add Condition"
            variant={Constants.variant.text}
          />

          {hasConditionOptions && (
            <Menu
              anchorEl={conditionAnchorEl}
              open={conditionOpen}
              onClose={handleConditionClose}
            >
              {conditionTagOptions?.map((opt) =>
                (
                  <MenuItem key={opt.key} onClick={insertCondition(opt.key)}>
                    {opt.value}
                  </MenuItem>
                ))}
            </Menu>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default EditorToolbar;
